import { NgOptimizedImage } from '@angular/common';
import { booleanAttribute, Component, computed, input, signal } from '@angular/core';
import { YouTubePlayer } from '@angular/youtube-player';

import type { PlayerVars } from 'youtube';

import { Button } from '@shared/components';
import { LazyViewportDirective } from '@shared/directives';

@Component({
  selector: 'app-youtube-player',
  imports: [YouTubePlayer, LazyViewportDirective, Button, NgOptimizedImage],
  templateUrl: './youtube-player.html',
  styleUrl: './youtube-player.scss',
  host: {
    '[class.borderless]': 'borderless()',
  },
})
export class YoutubePlayerComponent {
  readonly videoUrl = input.required<string>();
  readonly autoplay = input(false, { transform: booleanAttribute });
  readonly loop = input(false, { transform: booleanAttribute });
  readonly borderless = input(false, { transform: booleanAttribute });
  readonly poster = input<string>();
  readonly aspectRation = input<string>('16 / 9');

  /** Defers loading the YouTube IFrame API and iframe until the wrapper scrolls into view. */
  protected readonly visible = signal(false);

  /** Whether the poster has been clicked (or there was none) and the player should mount. */
  protected readonly started = signal(false);

  protected readonly showPlayer = computed(
    () => this.started() || (!this.poster() && this.visible()),
  );

  protected readonly videoId = computed(() => {
    const url = this.videoUrl();
    const match = url.match(/(?:youtu\.be\/|[?&]v=)([^&\s]+)/);
    return match?.[1] ?? '';
  });

  protected readonly playerVars = computed<PlayerVars>(() => ({
    autoplay: this.autoplay() ? 1 : 0,
    rel: 0,
    showinfo: 0,
    iv_load_policy: 3,
    mute: this.started() ? 0 : 1,
    modestbranding: 1,
  }));

  onPosterClick() {
    this.started.set(true);
  }

  onPlayerStateChange(event: YT.OnStateChangeEvent) {
    if (event.data === 0) {
      if (this.loop()) {
        event.target.playVideo();
      } else {
        this.started.set(false);
      }
    }
  }
}
